/*
  Etkie Home A — the behaviour the comp annotates but does not build.

  The source design carries three red `showNotes` callouts:
    "[ image & text slideshow of three primary features ]"  -> hero
    "[ auto play slideshow of testimonials ]"               -> testimonials
    "[ image changes on hover of location ]"                -> artists

  Each is implemented below as a small custom element so a section only opts in
  when the merchant has added more than one block.
*/

(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Shared: cycle [data-active] across a set of children on an interval,
     pausing on hover, on focus within, and when scrolled out of view. */
  class Rotator extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (this.slides.length < 2) return;

      // below the static breakpoint the extra slides are removed outright, so
      // their images are never fetched and nothing moves under the reader
      const staticBelow = Number(this.dataset.staticBelow || 0);
      if (staticBelow && window.matchMedia('(max-width: ' + (staticBelow - 1) + 'px)').matches) {
        this.slides.slice(1).forEach((s) => s.remove());
        const dots = this.querySelector('.etk-hero__dots');
        if (dots) dots.remove();
        this.slides = this.slides.slice(0, 1);
        return;
      }

      this.index = this.slides.findIndex((s) => s.hasAttribute('data-active'));
      if (this.index < 0) this.index = 0;
      this.interval = Number(this.dataset.interval || 6000);
      this.paused = false;

      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          this.show(i);
          this.restart();
        });
      });

      this.addEventListener('pointerenter', () => { this.paused = true; });
      this.addEventListener('pointerleave', () => { this.paused = false; });
      this.addEventListener('focusin', () => { this.paused = true; });
      this.addEventListener('focusout', () => { this.paused = false; });

      // don't animate off-screen — saves work and stops the slideshow
      // racing ahead while the visitor is further down the page
      this.visible = true;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => { this.visible = entry.isIntersecting; },
          { threshold: 0.2 }
        );
        this.observer.observe(this);
      }

      this.show(this.index);
      if (!reduceMotion.matches) this.restart();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      if (this.observer) this.observer.disconnect();
    }

    show(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, n) => {
        slide.toggleAttribute('data-active', n === this.index);
        slide.setAttribute('aria-hidden', n === this.index ? 'false' : 'true');
      });
      this.dots.forEach((dot, n) => {
        dot.setAttribute('aria-current', n === this.index ? 'true' : 'false');
      });
    }

    restart() {
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        if (!this.paused && this.visible) this.show(this.index + 1);
      }, this.interval);
    }
  }

  if (!customElements.get('etkie-rotator')) {
    customElements.define('etkie-rotator', Rotator);
  }

  /* Chapter: a horizontal scroll-snap track of two or three photographs. The
     sliding is the browser's — snap points and momentum swipe — and this only
     drives the arrows, the dots and the arrow keys, so the track degrades to a
     plain swipeable strip if the script never runs. */
  class Slider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (!this.track || this.slides.length === 0) return;

      // one slide has nothing to slide to: play its video, wire up nothing else
      if (this.slides.length < 2) {
        this.playing(this.slides[0], true);
        return;
      }

      this.dots = Array.from(this.querySelectorAll('[data-dot]'));

      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.go(i));
      });

      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(this.index - 1);
        }
      });

      // the swipe moves the track without going through go(), so the dots
      // follow where the track came to rest rather than the last button
      // pressed. It settles rather than tracking every frame: an rAF throttle
      // drops the last event of a run, which left a video slide scrolled into
      // place but paused, and smooth-scrolling past a video would start and
      // stop it on the way through.
      this.track.addEventListener('scroll', () => {
        clearTimeout(this.settle);
        this.settle = setTimeout(() => this.sync(this.nearest()), 120);
      });

      this.index = -1;
      this.sync(0);
    }

    disconnectedCallback() {
      clearTimeout(this.settle);
    }

    /* the slide whose left edge is closest to the track's scroll position */
    nearest() {
      const left = this.track.scrollLeft;
      let best = 0;
      let bestGap = Infinity;
      this.slides.forEach((slide, i) => {
        const gap = Math.abs(slide.offsetLeft - this.track.offsetLeft - left);
        if (gap < bestGap) {
          bestGap = gap;
          best = i;
        }
      });
      return best;
    }

    go(i) {
      const index = (i + this.slides.length) % this.slides.length;
      this.track.scrollTo({
        left: this.slides[index].offsetLeft - this.track.offsetLeft,
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
      });
      this.sync(index);
    }

    sync(index) {
      if (index === this.index) return;
      this.index = index;
      this.dots.forEach((dot, n) => {
        dot.setAttribute('aria-current', n === index ? 'true' : 'false');
      });
      this.slides.forEach((slide, n) => {
        slide.toggleAttribute('data-active', n === index);
        this.playing(slide, n === index);
      });
    }

    /* a video slide runs only while it is the one on screen — muted and looping,
       so the play() is allowed without a gesture. Under reduced motion it holds
       on its first frame instead. */
    playing(slide, active) {
      const video = slide.querySelector('video');
      if (!video) return;
      if (active && !reduceMotion.matches) {
        const played = video.play();
        if (played) played.catch(() => {});
      } else {
        video.pause();
      }
    }
  }

  if (!customElements.get('etkie-slider')) {
    customElements.define('etkie-slider', Slider);
  }

  /* Artists: the portrait cycles through the locations, and hovering one holds
     it there. Every location has a frame — its own image, or the section
     default standing in — so the rotation always matches the list. */
  class ArtistSwap extends HTMLElement {
    connectedCallback() {
      // the portrait is the only <img> inside the component; a hyphenated
      // data attribute cannot be passed through image_tag, so select it directly
      this.figure = this.querySelector('img');
      this.triggers = Array.from(this.querySelectorAll('[data-artist-src]'));
      if (!this.figure || !this.triggers.length) return;

      this.frames = this.triggers.map((t) => t.dataset.artistSrc);
      this.interval = Number(this.dataset.interval || 5000);
      this.index = 0;
      this.held = false;

      // decode up front so the first change doesn't flash
      this.frames.forEach((src) => { const pre = new Image(); pre.src = src; });

      // a pointer that cannot hover has no way to hold the rotation, so the
      // locations become controls: first tap picks the frame and stops the
      // rotation, a second tap on the same one follows the link
      this.touch = window.matchMedia('(hover: none)');

      this.triggers.forEach((trigger, i) => {
        trigger.addEventListener('pointerenter', () => {
          if (this.touch.matches) return;
          this.held = true;
          this.show(i);
        });
        trigger.addEventListener('focus', () => { this.held = true; this.show(i); });
        trigger.addEventListener('pointerleave', () => {
          if (this.touch.matches) return;
          this.held = false;
        });
        trigger.addEventListener('blur', () => { if (!this.touch.matches) this.held = false; });

        trigger.addEventListener('click', (e) => {
          if (!this.touch.matches) return;
          if (this.index === i && this.picked) return; // second tap follows the link
          e.preventDefault();
          this.picked = true;
          this.held = true;
          this.show(i);
        });
      });

      this.visible = true;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => { this.visible = entry.isIntersecting; },
          { threshold: 0.25 }
        );
        this.observer.observe(this);
      }

      this.show(0);
      if (this.frames.length > 1 && !reduceMotion.matches) {
        this.timer = setInterval(() => {
          if (!this.held && this.visible) this.show(this.index + 1);
        }, this.interval);
      }
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      if (this.observer) this.observer.disconnect();
    }

    show(i) {
      this.index = (i + this.frames.length) % this.frames.length;
      this.triggers.forEach((t, n) => t.toggleAttribute('data-current', n === this.index));
      const src = this.frames[this.index];
      if (!src || src === this.shown) return;

      // the first frame replaces the portrait outright, so the page never
      // loads into a fade
      if (this.shown === undefined) {
        this.shown = src;
        // srcset would override src, so it goes before the swap
        this.figure.removeAttribute('srcset');
        this.figure.removeAttribute('sizes');
        this.figure.setAttribute('src', src);
        return;
      }

      // crossfade: the next frame is laid over the current one and faded in,
      // then the layers beneath it are dropped. It waits for decode so a slow
      // image never fades in half-painted, and a newer call supersedes it.
      this.shown = src;
      const token = (this.token = (this.token || 0) + 1);
      const layer = document.createElement('img');
      layer.className = 'etk-artists__layer';
      layer.alt = '';
      layer.setAttribute('aria-hidden', 'true');
      layer.src = src;
      const frame = this.figure.parentElement;
      layer.decode().catch(() => {}).then(() => {
        if (token !== this.token) return;
        frame.appendChild(layer);
        const drop = () => {
          frame.querySelectorAll('.etk-artists__layer').forEach((l) => { if (l !== layer) l.remove(); });
        };
        if (reduceMotion.matches) { layer.classList.add('is-in'); drop(); return; }
        layer.addEventListener('transitionend', drop, { once: true });
        requestAnimationFrame(() => requestAnimationFrame(() => layer.classList.add('is-in')));
      });
    }
  }

  if (!customElements.get('etkie-artist-swap')) {
    customElements.define('etkie-artist-swap', ArtistSwap);
  }

  /* Tabs that swap the arrivals row in place. Every panel is already rendered;
     this only moves aria-selected and [hidden]. Arrow keys, Home and End move
     between tabs per the ARIA tabs pattern, and selecting a tab block in the
     theme editor opens its panel. */
  class Tabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
      if (this.tabs.length < 2) return;

      this.tabs.forEach((tab, n) => {
        tab.addEventListener('click', () => this.select(n));
        tab.addEventListener('keydown', (e) => {
          const last = this.tabs.length - 1;
          const to = { ArrowRight: n === last ? 0 : n + 1, ArrowLeft: n === 0 ? last : n - 1, Home: 0, End: last }[e.key];
          if (to === undefined) return;
          e.preventDefault();
          this.select(to);
          this.tabs[to].focus();
        });
      });

      this._onBlockSelect = (e) => {
        const n = this.tabs.findIndex((t) => t.id === `EtkTab-${e.detail.blockId}`);
        if (n !== -1) this.select(n);
      };
      document.addEventListener('shopify:block:select', this._onBlockSelect);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
    }

    select(index) {
      this.tabs.forEach((tab, n) => {
        const on = n === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
    }
  }

  if (!customElements.get('etkie-tabs')) {
    customElements.define('etkie-tabs', Tabs);
  }
})();

/*
  Header + mega menu behaviour.
  Sources: "Etkie Mega Menu*.dc.html" — desktop panels open on their trigger,
  mobile collapses to the drawer the two Mobile spec sheets describe
  (one material open at a time, 44pt-plus targets, sticky shop bar).
*/
(() => {
  class EtkieHeader extends HTMLElement {
    connectedCallback() {
      this.triggers = Array.from(this.querySelectorAll('[data-menu-trigger]'));
      this.panels = Array.from(this.querySelectorAll('[data-menu-panel]'));
      this.drawer = this.querySelector('.etk-drawer');

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => this.toggleMenu(trigger.dataset.menuTrigger));
      });

      // close on outside click and on Escape
      this._onDocClick = (e) => { if (!this.contains(e.target)) this.closeMenus(); };
      this._onKey = (e) => {
        if (e.key !== 'Escape') return;
        this.closeMenus();
        this.closeDrawer();
      };
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKey);

      this.querySelectorAll('[data-drawer-open]').forEach((b) =>
        b.addEventListener('click', () => this.openDrawer())
      );
      this.querySelectorAll('[data-drawer-close]').forEach((b) =>
        b.addEventListener('click', () => this.closeDrawer())
      );

      // drawer accordion — one section open at a time, per the mobile spec
      this.accordions = Array.from(this.querySelectorAll('[data-accordion]'));
      this.accordions.forEach((btn) => {
        btn.addEventListener('click', () => {
          const open = btn.getAttribute('aria-expanded') === 'true';
          this.accordions.forEach((other) => this.setAccordion(other, false));
          this.setAccordion(btn, !open);
        });
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKey);
      document.body.classList.remove('etk-no-scroll');
    }

    setAccordion(btn, open) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      const body = btn.nextElementSibling;
      if (body) body.toggleAttribute('hidden', !open);
      const sign = btn.querySelector('.etk-drawer__sign');
      if (sign) sign.textContent = open ? '−' : '+';
    }

    toggleMenu(name) {
      const isOpen = this.triggers.some(
        (t) => t.dataset.menuTrigger === name && t.getAttribute('aria-expanded') === 'true'
      );
      this.closeMenus();
      if (isOpen) return;
      this.triggers.forEach((t) => {
        if (t.dataset.menuTrigger === name) t.setAttribute('aria-expanded', 'true');
      });
      this.panels.forEach((p) => {
        if (p.dataset.menuPanel === name) p.removeAttribute('hidden');
      });
    }

    closeMenus() {
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      this.panels.forEach((p) => p.setAttribute('hidden', ''));
    }

    openDrawer() {
      if (!this.drawer) return;
      this.drawer.removeAttribute('hidden');
      document.body.classList.add('etk-no-scroll');
      const close = this.drawer.querySelector('.etk-drawer__close');
      if (close) close.focus();
    }

    closeDrawer() {
      if (!this.drawer || this.drawer.hasAttribute('hidden')) return;
      this.drawer.setAttribute('hidden', '');
      document.body.classList.remove('etk-no-scroll');
      const burger = this.querySelector('[data-drawer-open]');
      if (burger) burger.focus();
    }
  }

  if (!customElements.get('etkie-header')) {
    customElements.define('etkie-header', EtkieHeader);
  }
})();

/*
  Collection filter drawer.
  Source: "Etkie Mega Menu Mobile.dc.html", frame 03.

  Above 1000px the rail is the inline column the desktop comp draws and ticking
  a box submits immediately. Below it the rail is an overlay: selections batch
  up and the SHOW button submits them, so the page is not reloaded out from
  under the drawer on every tap.
*/
(() => {
  class FilterDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.etk-plp__panel');
      this.form = this.querySelector('#etk-filter-form');
      this.desktop = window.matchMedia('(min-width: 1001px)');

      this.opener = document.querySelector('[data-filter-open]');
      if (this.opener) this.opener.addEventListener('click', () => this.open());
      this.querySelectorAll('[data-filter-close]').forEach((b) =>
        b.addEventListener('click', () => this.close())
      );

      // desktop keeps the immediate-apply behaviour the inline rail implies
      if (this.form) {
        this.form.addEventListener('change', (e) => {
          if (!this.desktop.matches) return;
          if (!e.target.matches('input[type="checkbox"]')) return;
          if (this.form.requestSubmit) this.form.requestSubmit();
          else this.form.submit();
        });
      }

      this._onKey = (e) => { if (e.key === 'Escape') this.close(); };
      // a resize past the breakpoint must not leave the body locked
      this._onChange = () => { if (this.desktop.matches) this.close(); };
      this.desktop.addEventListener('change', this._onChange);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKey);
      this.desktop.removeEventListener('change', this._onChange);
      document.body.classList.remove('etk-no-scroll');
    }

    open() {
      if (this.desktop.matches) return;
      this.setAttribute('data-open', '');
      document.body.classList.add('etk-no-scroll');
      document.addEventListener('keydown', this._onKey);
      if (this.opener) this.opener.setAttribute('aria-expanded', 'true');
      const close = this.querySelector('.etk-plp__panel-close');
      if (close) close.focus();
    }

    close() {
      if (!this.hasAttribute('data-open')) return;
      this.removeAttribute('data-open');
      document.body.classList.remove('etk-no-scroll');
      document.removeEventListener('keydown', this._onKey);
      if (this.opener) {
        this.opener.setAttribute('aria-expanded', 'false');
        this.opener.focus();
      }
    }
  }

  if (!customElements.get('etkie-filter-drawer')) {
    customElements.define('etkie-filter-drawer', FilterDrawer);
  }
})();

/*
  PDP.
  Source: "Etkie PDP.dc.html" — gallery frames, accordion, set upsell, and the
  sticky bar the comp notes is "the highest-impact single fix on the site".
*/
(() => {
  class EtkiePDP extends HTMLElement {
    connectedCallback() {
      this.frames = Array.from(this.querySelectorAll('[data-frame]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.caption = this.querySelector('[data-caption]');
      this.counter = this.querySelector('[data-counter]');

      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.showFrame(i)));

      // accordion — the comp keeps one panel open at a time
      this.panels = Array.from(this.querySelectorAll('[data-panel]'));
      this.panels.forEach((btn) => {
        btn.addEventListener('click', () => {
          const open = btn.getAttribute('aria-expanded') === 'true';
          this.panels.forEach((other) => this.setPanel(other, false));
          this.setPanel(btn, !open);
        });
      });

      this.setupVariants();
      this.setupQty();
      this.setupSwipe();
      this.setupPanelLinks();
      this.setupSet();
      this.setupLaunchSet();
      this.setupBar();
      this.setupFit();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.fitObserver) this.fitObserver.disconnect();
    }

    showFrame(i, fromSwipe) {
      if (!this.frames.length) return;
      const n = (i + this.frames.length) % this.frames.length;
      const stage = this.querySelector('[data-stage]');
      if (!fromSwipe && stage && stage.scrollWidth > stage.clientWidth) {
        stage.scrollTo({ left: n * stage.clientWidth, behavior: 'smooth' });
      }
      this.frames.forEach((f, k) => f.toggleAttribute('data-active', k === n));
      this.thumbs.forEach((t, k) => t.setAttribute('aria-current', k === n ? 'true' : 'false'));
      const thumb = this.thumbs[n];
      if (this.caption && thumb) this.caption.textContent = thumb.dataset.captionText || '';
      if (this.counter) this.counter.textContent = (n + 1) + ' / ' + this.frames.length;
    }

    // − / + around the number field; the field itself stays typeable
    setupQty() {
      const wrap = this.querySelector('[data-qty]');
      if (!wrap) return;
      const input = wrap.querySelector('input');
      wrap.querySelectorAll('[data-qty-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const max = parseInt(input.max, 10) || Infinity;
          const next = (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.qtyStep, 10);
          input.value = Math.min(max, Math.max(1, next));
        });
      });
    }

    // On phones the stage becomes a swipeable strip (CSS scroll-snap), so the
    // thumbnail row can go. This keeps the counter and caption in step with
    // whichever frame has snapped into view.
    setupSwipe() {
      const stage = this.querySelector('[data-stage]');
      if (!stage) return;
      let tick = null;
      stage.addEventListener('scroll', () => {
        if (tick) return;
        tick = requestAnimationFrame(() => {
          tick = null;
          if (stage.scrollWidth <= stage.clientWidth) return;
          const n = Math.round(stage.scrollLeft / stage.clientWidth);
          if (n !== this.swipeIndex) {
            this.swipeIndex = n;
            this.showFrame(n, true);
          }
        });
      }, { passive: true });
    }

    // a link to #etk-panel-<id> (the size row's "how to put it on") opens
    // that panel rather than landing on a closed heading
    setupPanelLinks() {
      this.querySelectorAll('a[href^="#etk-panel-"]').forEach((a) => {
        a.addEventListener('click', (e) => {
          const target = this.querySelector(a.getAttribute('href'));
          const btn = target && target.querySelector('[data-panel]');
          if (!btn) return;
          e.preventDefault();
          this.panels.forEach((other) => this.setPanel(other, other === btn));
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }

    setPanel(btn, open) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      const body = btn.nextElementSibling;
      if (body) body.toggleAttribute('hidden', !open);
      const sign = btn.querySelector('.etk-pdp__panel-sign');
      if (sign) sign.textContent = open ? '−' : '+';
    }

    setupVariants() {
      const buttons = Array.from(this.querySelectorAll('[data-variant]'));
      if (!buttons.length) return;
      const input = this.querySelector('[data-variant-input]');
      const price = this.querySelector('[data-price]');
      const atcPrice = this.querySelector('[data-atc-price]');
      const barPrices = this.querySelectorAll('[data-bar-price]');

      buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.hasAttribute('disabled')) return;
          buttons.forEach((b) => b.setAttribute('aria-checked', b === btn ? 'true' : 'false'));
          if (input) input.value = btn.dataset.variantId;
          const p = btn.dataset.variantPrice;
          if (p) {
            // the compare-at span is a child of the price node, so only its
            // first text node is replaced
            if (price && price.firstChild) price.firstChild.textContent = p;
            if (atcPrice) atcPrice.textContent = p;
            barPrices.forEach((el) => { el.textContent = p; });
          }
          // the launch set follows the size: its bundle variant, or none
          this.syncLaunchSet();
        });
      });
    }

    // The launch set card (the cape with its free cuff): a Shopify Bundle,
    // one variant per size. Ticked, the form's id is the bundle variant for
    // the chosen size; unticked, or once that size's bundle is sold out, the
    // product's own variant goes in and the card hides. Each size button
    // carries its bundle variant's id, price, availability and count.
    setupLaunchSet() {
      this.launch = this.querySelector('[data-launch-set]');
      if (!this.launch) return;
      this.launch.addEventListener('click', () => {
        const on = this.launch.getAttribute('aria-pressed') === 'true';
        this.launch.setAttribute('aria-pressed', on ? 'false' : 'true');
        this.syncLaunchSet();
      });
      this.syncLaunchSet();
    }

    syncLaunchSet() {
      const card = this.launch;
      const input = this.querySelector('[data-variant-input]');
      if (!card || !input) return;
      const checked = this.querySelector('[data-variant][aria-checked="true"]');
      // one size only: the card itself carries the bundle data
      const src = checked || card;
      const available = src.dataset.setAvailable === 'true' && !!src.dataset.setVariantId;
      const on = available && card.getAttribute('aria-pressed') === 'true';

      card.toggleAttribute('hidden', !available);
      const sign = card.querySelector('[data-set-sign]');
      if (sign) sign.textContent = on ? '✓' : '+';
      const left = card.querySelector('[data-set-left-text]');
      const leftWrap = card.querySelector('[data-set-left-wrap]');
      if (left) left.textContent = src.dataset.setLeft || '';
      if (leftWrap) leftWrap.toggleAttribute('hidden', !src.dataset.setLeft);

      input.value = on ? src.dataset.setVariantId : (checked ? checked.dataset.variantId : input.dataset.baseId);
      const p = on ? src.dataset.setPrice : (checked ? checked.dataset.variantPrice : input.dataset.basePrice);
      if (!p) return;
      const atcPrice = this.querySelector('[data-atc-price]');
      if (atcPrice) atcPrice.textContent = p;
      this.querySelectorAll('[data-bar-price]').forEach((el) => { el.textContent = p; });
    }

    setupSet() {
      const toggle = this.querySelector('[data-set-toggle]');
      const input = this.querySelector('[data-set-input]');
      if (!toggle || !input) return;
      toggle.addEventListener('click', () => {
        const on = toggle.getAttribute('aria-pressed') === 'true';
        toggle.setAttribute('aria-pressed', on ? 'false' : 'true');
        input.disabled = on;
        const sign = toggle.querySelector('[data-set-sign]');
        if (sign) sign.textContent = on ? '+' : '−';
      });
    }

    setupBar() {
      const bar = this.querySelector('[data-bar-el]');
      const atc = this.querySelector('[data-atc]');
      if (!bar || !atc || !('IntersectionObserver' in window)) return;

      // the comp shows the bar once the gallery and its buy button scroll past
      this.observer = new IntersectionObserver(
        ([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.toggleAttribute('data-shown', show);
          bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        },
        { threshold: 0 }
      );
      this.observer.observe(atc);
    }

    // the fitting film plays only while it is on screen, and never on its own
    // under reduced motion. Pausing by hand keeps it paused through scrolling.
    setupFit() {
      const video = this.querySelector('.etk-pdp__fit-media video');
      const toggle = this.querySelector('[data-fit-toggle]');
      if (!video || !toggle) return;
      const label = toggle.querySelector('[data-fit-label]');
      let held = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const sync = () => {
        const playing = !video.paused;
        toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
        if (label) label.textContent = playing ? toggle.dataset.pause : toggle.dataset.play;
      };
      const play = () => { video.play().catch(() => {}); };

      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      const flip = () => {
        held = !video.paused;
        if (held) video.pause(); else play();
      };
      toggle.addEventListener('click', flip);
      video.addEventListener('click', flip);

      if (!('IntersectionObserver' in window)) return;
      let inView = false;
      this.fitObserver = new IntersectionObserver(
        ([entry]) => {
          inView = entry.isIntersecting;
          if (!inView) video.pause();
          else if (!held) play();
        },
        { threshold: 0.4 }
      );
      this.fitObserver.observe(video);

      // the browser pauses muted video in a background tab and does not
      // resume it, so pick it back up when the tab returns
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && inView && !held) play();
      });
    }
  }

  if (!customElements.get('etkie-pdp')) customElements.define('etkie-pdp', EtkiePDP);

  // Related products on the PDP. Shopify only fills recommendations when it
  // renders the section through its recommendations endpoint, so the page
  // ships an empty shell and this swaps in the filled one once it is near
  // the viewport. An empty response leaves the shell empty.
  class EtkieRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.children.length) return;
      const load = () => {
        fetch(this.dataset.url)
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('etkie-recommendations');
            if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
          })
          .catch(() => {});
      };
      if (!('IntersectionObserver' in window)) return load();
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { io.disconnect(); load(); }
      }, { rootMargin: '600px 0px' });
      io.observe(this);
    }
  }

  if (!customElements.get('etkie-recommendations')) customElements.define('etkie-recommendations', EtkieRecommendations);
})();

/*
  Waitlist signup (etkie-story-waitlist, etkie-interview). Subscribes the email
  to a Klaviyo list through Klaviyo's client subscriptions API, the same public
  endpoint Klaviyo's own forms use.

  When the element carries a [data-friend] block, someone already on the list
  sees that instead of the form: anyone who signed up in this browser, or whom
  Klaviyo's onsite script has identified (arriving from one of our emails).
*/
(() => {
  const KEY = 'etk:on-list';

  const remembered = () => {
    try { return localStorage.getItem(KEY) === '1'; } catch (e) { return false; }
  };
  const remember = () => {
    try { localStorage.setItem(KEY, '1'); } catch (e) { /* private window */ }
  };

  class EtkieWaitlist extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-form]');
      this.email = this.querySelector('[data-email]');
      this.button = this.querySelector('[data-submit]');
      this.status = this.querySelector('[data-status]');
      this.friend = this.querySelector('[data-friend]');

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submit();
      });

      if (this.friend) {
        this.friend.querySelector('[data-share]')?.addEventListener('click', () => this.share());
        this.recognise();
      }
    }

    async recognise() {
      if (remembered()) { this.showFriend(); return; }
      // the onsite embed defines window.klaviyo; isIdentified resolves once it has loaded
      try {
        if (window.klaviyo?.isIdentified && (await window.klaviyo.isIdentified())) this.showFriend();
      } catch (e) { /* no Klaviyo on the page: keep the form */ }
    }

    showFriend() {
      if (!this.friend) return;
      this.form.hidden = true;
      this.friend.hidden = false;
    }

    async share() {
      const title = document.title;
      const url = this.friend.dataset.url || location.href.split('#')[0];
      if (navigator.share) {
        try { await navigator.share({ title, url }); } catch (e) { /* dismissed */ }
        return;
      }
      location.href = 'mailto:?subject=' + encodeURIComponent(title) + '&body=' + encodeURIComponent(url);
    }

    say(key) {
      const t = this.querySelector('[data-msg="' + key + '"]');
      this.status.textContent = t ? t.innerHTML.trim() : '';
      this.status.hidden = false;
      this.status.dataset.state = key;
    }

    async submit() {
      const email = this.email.value.trim();
      if (!this.email.checkValidity() || !email) { this.say('invalid'); this.email.focus(); return; }
      const { site, list, source } = this.dataset;
      if (!site || !list) { this.say('closed'); return; }

      this.button.disabled = true;
      try {
        const res = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(site), {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: source || 'Wool story page',
                profile: { data: { type: 'profile', attributes: { email } } }
              },
              relationships: { list: { data: { type: 'list', id: list } } }
            }
          })
        });
        if (res.ok) {
          remember();
          this.form.hidden = true;
          this.say('ok');
        } else {
          this.say('error');
        }
      } catch (err) {
        this.say('error');
      } finally {
        this.button.disabled = false;
      }
    }
  }

  if (!customElements.get('etkie-waitlist')) customElements.define('etkie-waitlist', EtkieWaitlist);
})();

/*
  Collection pages: the rail's search box filters the grid in place instead of
  leaving for /search (Maya's review). Cards whose name does not contain every
  word typed are hidden, and the "N PIECES" count follows. The grid is
  paginated, so the first keystroke pulls the remaining pages in behind the
  scenes (the same next-page URLs Load More uses, so ticked filters still
  apply) and the search covers the whole collection. Without JS, or on
  Enter in a browser without it, the form still submits to /search.
*/
(() => {
  document.querySelectorAll('.etk-plp .etk-plp__search').forEach((form) => {
    const section = form.closest('.etk-plp');
    const grid = section && section.querySelector('.etk-plp__grid');
    const input = form.querySelector('input[name="q"]');
    if (!grid || !input) return;

    const countEl = section.querySelector('.etk-plp__count');
    const countNode = countEl && Array.from(countEl.childNodes).find((n) => n.nodeType === 3 && /\d/.test(n.textContent));
    const countText = countNode ? countNode.textContent : '';

    const note = document.createElement('p');
    note.className = 'etk-body etk-plp__nomatch';
    note.hidden = true;
    grid.after(note);

    let loading = null;
    const loadAll = () => {
      if (loading) return loading;
      loading = (async () => {
        const more = section.querySelector('.etk-plp__more');
        let next = more && more.querySelector('a') ? more.querySelector('a').getAttribute('href') : null;
        while (next) {
          const res = await fetch(next, { credentials: 'same-origin' });
          if (!res.ok) return;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const page = doc.querySelector('.etk-plp__grid');
          if (!page) return;
          page.querySelectorAll(':scope > .etk-card').forEach((card) => grid.append(document.importNode(card, true)));
          const nextLink = doc.querySelector('.etk-plp__more a');
          next = nextLink ? nextLink.getAttribute('href') : null;
        }
        // everything is in the grid now, so Load More has nothing left to load
        if (more) more.remove();
      })().catch(() => {});
      return loading;
    };

    const apply = () => {
      const words = input.value.toLowerCase().trim().split(/\s+/).filter(Boolean);
      let shown = 0;
      grid.querySelectorAll(':scope > .etk-card').forEach((card) => {
        const title = card.querySelector('.etk-card__title');
        const name = (title ? title.textContent : '').toLowerCase();
        const hit = words.every((w) => name.includes(w));
        // .etk-card is display:flex, which would override the hidden attribute
        card.style.display = hit ? '' : 'none';
        if (hit) shown += 1;
      });
      if (countNode) countNode.textContent = words.length ? countText.replace(/\d[\d,]*/, String(shown)) : countText;
      const more = section.querySelector('.etk-plp__more');
      if (more) more.style.display = words.length ? 'none' : '';

      note.textContent = '';
      note.hidden = !(words.length && shown === 0);
      if (!note.hidden) {
        note.append('Nothing here matches “' + input.value.trim() + '”. ');
        const link = document.createElement('a');
        link.href = form.getAttribute('action') + '?type=product&q=' + encodeURIComponent(input.value.trim());
        link.className = 'etk-link';
        link.textContent = 'Search the whole shop →';
        note.append(link);
      }
    };

    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        apply();
        if (input.value.trim()) loadAll().then(apply);
      }, 120);
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      apply();
      if (input.value.trim()) loadAll().then(apply);
    });
  });
})();

/*
  Collection pages: Load More adds the next page's cards to this grid instead
  of navigating to ?page=N (Maya's review). The fetched page's own button
  supplies the next link and the remaining count; on the last page the button
  goes. The link still works as a plain link without JS.
*/
(() => {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.etk-plp__more-btn');
    if (!btn || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    const section = btn.closest('.etk-plp');
    const grid = section && section.querySelector('.etk-plp__grid');
    if (!grid) return;
    e.preventDefault();
    if (btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    const label = btn.textContent;
    btn.textContent = 'Loading…';
    try {
      const res = await fetch(btn.getAttribute('href'), { credentials: 'same-origin' });
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const page = doc.querySelector('.etk-plp__grid');
      if (!page) throw new Error('no grid');
      page.querySelectorAll(':scope > .etk-card').forEach((card) => grid.append(document.importNode(card, true)));
      const next = doc.querySelector('.etk-plp__more-btn');
      if (next) {
        btn.setAttribute('href', next.getAttribute('href'));
        btn.textContent = next.textContent;
        btn.removeAttribute('aria-busy');
      } else {
        btn.closest('.etk-plp__more').remove();
      }
    } catch (err) {
      // fall back to the page itself rather than leave a dead button
      window.location.href = btn.getAttribute('href');
      btn.textContent = label;
    }
  });
})();
